import { useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { FormTemplate, FormSection } from "../types/forms";
import { getPublicForm, submitPublicForm } from "../services/consultationform";
import UserPreview from "./forms/[id]/builder/UserPreview";
import { Button } from "../components/ui/Button";
import { useToast } from "../hooks/useToast";
import { Loader2, X, CheckCircle2 } from "lucide-react";

const mapSectionType = (section: any): FormSection => {
    if (section.type) return section as FormSection;

    const templateIdMap: Record<number, FormSection["type"]> = {
        1: "contact",
        2: "medical",
        3: "marketing",
        4: "question",
        5: "heading",
        6: "text",
        7: "policy",
        8: "disclaimer",
    };

    return {
        ...section,
        type: section.form_template_id ? templateIdMap[section.form_template_id] || null : null
    } as FormSection;
};

const deriveSectionTitle = (s: FormSection): string | null => {
    let sectionTitle = s.title ?? null;
    if ((s.type === "text" || s.type === "heading") && !sectionTitle?.trim() && (s.fields?.length ?? 0) > 0) {
        const first = s.fields![0];
        if (typeof first.options === "string" && first.options.trim()) sectionTitle = first.options.trim();
        else if (first.label?.trim()) sectionTitle = first.label.trim();
        else if (first.options && typeof first.options === "object" && first.options !== null && "content" in first.options) {
            const opts = first.options as { content?: string };
            if (opts.content?.trim()) sectionTitle = opts.content.trim();
        }
    }
    return sectionTitle;
};

export default function PublicConsultationPage() {
    const { encodedId } = useParams<{ encodedId: string }>();
    const [template, setTemplate] = useState<FormTemplate | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formValues, setFormValues] = useState<Record<string, any>>({});
    const showToast = useToast();

    useEffect(() => {
        if (!encodedId) return;

        const fetchTemplate = async () => {
            try {
                setLoading(true);
                // Decode ID
                let decodedId: string;
                try {
                    decodedId = atob(encodedId);
                } catch (e) {
                    decodedId = encodedId; // Fallback if not base64
                }

                const res = await getPublicForm(decodedId);
                if (res?.form) {
                    const mappedForm: FormTemplate = {
                        ...res.form,
                        sections: (res.form.sections || [])
                            .map((s: FormSection) => {
                                const mapped = mapSectionType(s);
                                return { ...mapped, title: deriveSectionTitle(mapped) ?? mapped.title };
                            })
                            .sort((a: FormSection, b: FormSection) => (a.order || 0) - (b.order || 0))
                    };
                    setTemplate(mappedForm);
                } else {
                    setError("Form not found");
                }
            } catch (err: any) {
                setError(err.message || "Failed to load form");
            } finally {
                setLoading(false);
            }
        };

        fetchTemplate();
    }, [encodedId]);

    const handleValueChange = (sectionId: string | number, value: any) => {
        setFormValues(prev => ({
            ...prev,
            [sectionId]: value
        }));
    };

    const handleSubmit = async () => {
        if (!template || submitting) return;

        try {
            setSubmitting(true);

            const payload = {
                answers: formValues,
            };

            let decodedId = encodedId;
            try { decodedId = atob(encodedId!); } catch (e) { }

            await submitPublicForm(decodedId!, payload);
            showToast.success("Form submitted successfully!");
            setSubmitted(true);
            setFormValues({}); // Reset form
        } catch (err: any) {
            if (err.type === "serverValidation" && err.errors) {
                const errorList = Object.values(err.errors);
                showToast.error(
                    <div className="flex flex-col gap-2 py-1">
                        <div className="flex items-center gap-2 text-red-700 font-semibold border-b border-red-100 pb-1 mb-1">
                            <span className="text-lg">Validation Failed</span>
                        </div>
                        <ul className="space-y-1.5">
                            {errorList.map((msg: any, i: number) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-red-600/90 leading-tight">
                                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                                    {String(msg)}
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            } else {
                showToast.error(err.message || "Failed to submit form");
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen text-gray-500 bg-gray-50">
                <Loader2 className="w-8 h-8 animate-spin mb-2 text-[#885ABB]" />
                <p className="animate-pulse">Loading consultation form...</p>
            </div>
        );
    }

    if (error || !template) {
        return (
            <div className="flex items-center justify-center min-h-screen p-8 text-center text-red-500 bg-gray-50">
                <div className="bg-white p-10 rounded-2xl shadow-xl border border-red-100 max-w-md w-full">
                    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <X className="w-8 h-8 text-red-500" />
                    </div>
                    <h2 className="text-2xl font-bold mb-2 text-gray-800">Error</h2>
                    <p className="text-gray-600">{error || "Form could not be loaded."}</p>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="flex items-center justify-center min-h-screen p-8 text-center bg-gray-50">
                <div className="bg-white p-10 rounded-2xl shadow-xl border border-green-50 max-w-md w-full animate-in fade-in zoom-in duration-300">
                    <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
                            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                    </div>
                    <h2 className="text-3xl font-bold mb-4 text-gray-900">Thank You!</h2>
                    <p className="text-gray-600 mb-8 leading-relaxed">
                        Your consultation form has been successfully submitted. We appreciate your time and feedback.
                    </p>
                    <Button
                        onClick={() => setSubmitted(false)}
                        className="w-full bg-[#885ABB] hover:bg-[#764da3] text-white h-12 rounded-xl font-semibold shadow-lg shadow-purple-200 transition-all hover:scale-[1.02]"
                    >
                        Submit Another Response
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen overflow-y-auto bg-gray-50 py-12 px-4">
            <div className="max-w-3xl mx-auto space-y-8">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden pb-8">
                    <UserPreview
                        template={template}
                        formValues={formValues}
                        onFormValueChange={handleValueChange}
                        showSubmit={false}
                    />

                    <div className="px-8 flex justify-end">
                        <Button
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="bg-[#885ABB] hover:bg-[#764da3] text-white px-8 h-11"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Submitting...
                                </>
                            ) : (
                                "Submit Form"
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
